import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  CircularProgress,
} from '@mui/material';
import { Campaign, Add, PriorityHigh } from '@mui/icons-material';
import { orderBy } from 'firebase/firestore';
import { format } from 'date-fns';
import { ko } from 'date-fns/locale';
import { MainLayout } from '../components/layout/MainLayout';
import { useFirestoreQuery } from '../hooks/useFirestoreQuery';
import { Notice } from '../types/notice';

export const NoticePage: React.FC = () => {
  const navigate = useNavigate();

  const { data: notices, loading } = useFirestoreQuery<Notice>(
    'notices',
    orderBy('createdAt', 'desc')
  );

  const handleNoticeClick = (noticeId: string) => {
    navigate(`/notices/${noticeId}`);
  };

  const handleCreateNotice = () => {
    navigate('/notices/new');
  };

  return (
    <MainLayout>
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Campaign sx={{ fontSize: 32, color: '#667eea' }} />
            <Box>
              <Typography variant="h4" component="h1" fontWeight={700}>
                공지사항
              </Typography>
              <Typography variant="body2" color="text.secondary">
                사용자에게 전달할 공지사항을 관리합니다.
              </Typography>
            </Box>
          </Box>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleCreateNotice}
            size="large"
          >
            새 공지 작성
          </Button>
        </Box>

        {/* 공지사항 목록 테이블 */}
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f8fafc' }}>
                  <TableCell sx={{ fontWeight: 600, width: '60%' }}>제목</TableCell>
                  <TableCell sx={{ fontWeight: 600, width: '25%' }}>작성일</TableCell>
                  <TableCell sx={{ fontWeight: 600, width: '15%', textAlign: 'center' }}>
                    상태
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={3} align="center" sx={{ py: 8 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : notices.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} align="center" sx={{ py: 8 }}>
                      <Typography color="text.secondary">
                        등록된 공지사항이 없습니다.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  notices.map((notice) => (
                    <TableRow
                      key={notice.id}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => handleNoticeClick(notice.id)}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {notice.isUrgent && (
                            <PriorityHigh sx={{ color: '#ef4444', fontSize: 20 }} />
                          )}
                          <Typography
                            variant="body2"
                            fontWeight={notice.isUrgent ? 600 : 500}
                            sx={{
                              color: notice.isUrgent ? '#ef4444' : 'inherit',
                            }}
                          >
                            {notice.title}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {notice.createdAt
                          ? format(notice.createdAt.toDate(), 'yyyy-MM-dd HH:mm', {
                              locale: ko,
                            })
                          : '-'}
                      </TableCell>
                      <TableCell align="center">
                        {notice.isUrgent && (
                          <Chip
                            label="긴급"
                            color="error"
                            size="small"
                            variant="outlined"
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>
    </MainLayout>
  );
};
